// app/providers/AuthProvider.js
import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import * as authService from '../../services/auth';
import { resetPassword } from '../../services/users';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const [tempUser, setTempUser] = useState(null);

  useEffect(() => {
    const initAuth = () => {
      try {
        const storedToken = localStorage.getItem('accessToken');
        const storedUser = authService.getCurrentUser();

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(storedUser);
          
          // Verificar si el usuario necesita resetear contraseña
          if (storedUser.resetPassword) {
            setNeedsPasswordReset(true);
            setTempUser(storedUser);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (credentials) => {
    try {
      const response = await authService.login(credentials);
      
      const { accessToken: newToken,refreshToken: refreshToken, user: newUser, token: responseToken } = response;

      // El servicio puede retornar 'token' o 'accessToken'
      const finalToken = newToken || responseToken;
      const finalUser = newUser || response.user;

      // Verificar si necesita resetear contraseña
      if (finalUser?.resetPassword) {
        // Guardar temporalmente
        setTempUser(finalUser);
        setNeedsPasswordReset(true);
        // NO establecer token ni user aún
        return { 
          success: true, 
          needsPasswordReset: true, 
          user: finalUser 
        };
      }

      // Si no necesita reset, autenticar normalmente
      setToken(finalToken);
      setUser(finalUser);
      setNeedsPasswordReset(false);
      setTempUser(null);

      localStorage.setItem('accessToken', finalToken);
      localStorage.setItem('refreshToken',refreshToken);
      localStorage.setItem('user', JSON.stringify(finalUser));
      
      return { 
        success: true, 
        needsPasswordReset: false, 
        user: finalUser 
      };
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, []);

  const completePasswordReset = useCallback(async (resetData) => {
    try {
      // Llamar al servicio de reset de contraseña
      const response = await resetPassword(resetData);
      
      // Ahora hacer login con las nuevas credenciales
      const loginResponse = await authService.login({
        email: tempUser.email,
        password: resetData.newPassword
      });
      
      const { accessToken: newToken, user: newUser, token: responseToken } = loginResponse;
      const finalToken = newToken || responseToken;
      const finalUser = newUser || loginResponse.user;

      // Actualizar estado de autenticación
      setToken(finalToken);
      setUser(finalUser);
      setNeedsPasswordReset(false);
      setTempUser(null);

      localStorage.setItem('accessToken', finalToken);
      localStorage.setItem('user', JSON.stringify(finalUser));
      
      return response;
    } catch (error) {
      console.error('Password reset failed:', error);
      throw error;
    }
  }, [tempUser]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setNeedsPasswordReset(false);
      setTempUser(null);
      
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
    }
  }, []);

  const clearPasswordReset = useCallback(() => {
    setNeedsPasswordReset(false);
    setTempUser(null);
    // También limpiar localStorage si es necesario
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
  }, []);

  // Usar useMemo para recalcular isAuthenticated cuando token cambie
  const isAuthenticated = useMemo(() => {
    return !!token && !needsPasswordReset;
  }, [token, needsPasswordReset]);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated,
    needsPasswordReset,
    tempUser,
    login,
    logout,
    completePasswordReset,
    clearPasswordReset,
  }), [user, token, loading, isAuthenticated, needsPasswordReset, tempUser, login, logout, completePasswordReset, clearPasswordReset]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;